import React, { FunctionComponent, useMemo } from 'react';
import { Button, Space, Table } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons/lib';
import { TableComponentProps } from '../../interfaces/table-component-props.interface';
import { SupplyDto } from '@tek-crm/backend/dist/application/supply/dto/supply.dto';
import { remove } from '../../api/counterparty.api';
import { useRemoveHandler } from '../../hooks/use-remove-handler';
import { PageWrapper } from '../shared/styled';
import { useAccessRule } from '../../hooks/use-access-rule';
import { AccessAttributeDomain } from '@tek-crm/backend/dist/domain/user/types/access-attribute.type';

const useCounterpartyColumns = ({ pagination, openTab, refresh }) => {
  const createRemoveHandler = useRemoveHandler({
    remove,
    refresh,
    description: 'Контрагент был успешно удалён',
  });

  const viewPermission = useAccessRule({
    domain: AccessAttributeDomain.Counterparty,
    requestedPermission: 'view',
  });

  const managePermission = useAccessRule({
    domain: AccessAttributeDomain.Counterparty,
    requestedPermission: 'manage',
  });

  return useMemo(
    () => [
      {
        title: 'Тип',
        dataIndex: 'type',
        sorter: true,
        render: value => {
          switch (value) {
            case 'supplier':
              return 'Поставщик';
            case 'manufacturer':
              return 'Завод-изготовитель';
            case 'customer':
              return 'Заказчик';
            default:
              return 'Не известен';
          }
        },
      },
      {
        title: 'Название',
        dataIndex: 'name',
        sorter: true,
      },
      {
        title: 'Адрес',
        dataIndex: 'address',
      },
      {
        title: 'Действия',
        key: 'action',
        fixed: 'right' as const,
        width: 100,
        render: (text, record) => (
          <Space size="middle">
            <Button
              type="dashed"
              disabled={!viewPermission}
              icon={<EditOutlined />}
              onClick={() => openTab({ type: 'edit', key: record.id })}
            />
            <Button
              danger
              type="dashed"
              disabled={!managePermission}
              icon={<DeleteOutlined />}
              onClick={createRemoveHandler(record.id)}
            />
          </Space>
        ),
      },
    ],
    [pagination, openTab],
  );
};

export const CounterpartyTable: FunctionComponent<TableComponentProps<
  SupplyDto
>> = ({ state, onTableChange, show, openTab, refresh }) => {
  const columns = useCounterpartyColumns({
    pagination: state.pagination,
    openTab,
    refresh,
  });

  const managePermission = useAccessRule({
    domain: AccessAttributeDomain.Counterparty,
    requestedPermission: 'manage',
  });

  if (!show) {
    return null;
  }

  return (
    <PageWrapper>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Button
          block
          type="primary"
          disabled={!managePermission}
          onClick={() => openTab({ key: 'add' })}
        >
          Добавить
        </Button>
        <Table
          scroll={{ x: 700 }}
          columns={columns}
          rowKey={record => record.id}
          dataSource={state.data}
          pagination={state.pagination}
          loading={state.loading}
          onChange={onTableChange}
        />
      </Space>
    </PageWrapper>
  );
};
